<!-- src/components/ImpersonationBanner.vue -->
<!--
  Shown across the top whenever the session belongs to somebody else.

  Not decoration. Everything in the app — every list, every save, every
  delete — is operating inside a customer's real data, and the only
  thing distinguishing it from being logged in as yourself is this
  strip. A support person who forgets which account they are in will
  edit the wrong customer's products, and the audit log will record it
  as the customer doing it to themselves.

  Hence: always visible, red, names the customer, and carries its own
  way out. It deliberately does not collapse or auto-hide.
-->
<template>
  <v-sheet
    v-if="isImpersonating"
    color="error"
    class="d-flex align-center px-4 py-2"
    style="position: sticky; top: 0; z-index: 2000;"
  >
    <v-icon color="white" class="mr-3">mdi-incognito</v-icon>

    <div class="flex-grow-1 text-white">
      <div class="text-body-2 font-weight-medium">
        {{ t('impersonation.bannerTitle', { customer: customerName }) }}
      </div>
      <div class="text-caption" style="opacity: 0.85">
        {{ t('impersonation.bannerSubtitle', { user: originalName }) }}
      </div>
    </div>

    <v-btn
      variant="flat"
      color="white"
      size="small"
      :loading="ending"
      prepend-icon="mdi-exit-to-app"
      @click="stop"
    >
      {{ t('impersonation.stop') }}
    </v-btn>
  </v-sheet>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const { t }    = useI18n();
const store    = useStore();
const router   = useRouter();
const ending   = ref(false);

const isImpersonating = computed(() => store.getters['auth/isImpersonating']);
const customerName    = computed(() => store.getters['auth/customerName']);
const originalName    = computed(() => store.getters['auth/originalUser']?.username || '');

const stop = async () => {
  ending.value = true;
  await store.dispatch('auth/endImpersonation');
  ending.value = false;

  // Back to the dashboard rather than staying put: the current route
  // may be a customer-scoped page that the god account has no data for,
  // and landing on an empty list is a confusing way to find out the
  // session ended.
  router.push('/');
};
</script>